/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import { PropGetters } from "downshift";
import { Fragment, HTMLAttributes, Key, RefAttributes, useCallback, useEffect, useMemo, useRef } from "react";
import { VariableSizeList } from "react-window";
import { useI18n } from "../../../../i18n/i18n";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { Flyout, FlyoutPlacement, FlyoutProps } from "../../../flyout";
import { menuTheme } from "../../../menu/styled/menuTheme";
import { BaseOptionType, FormatOption, OptionSpreadProps } from "../../types";
import {
    isSelectAllMarkerOption,
    selectAllMarkerOption,
    stopPropagationHandler,
    isCreatableMarkerOption,
    creatableMarkerOption,
    isSelectV2OptionSeparator,
    selectV2OptionSeparator,
} from "../../utils";
import { CreatableOption } from "../CreatableOption";
import { SelectAllOption } from "../multiselect/SelectAllOption";
import { LoadingOption } from "../shared/LoadingOption";
import { NoResultsMessage } from "../shared/NoResultsMessage";
import { VariableSizeListStyled } from "./VariableSizeListStyled";
import { VirtualizedOption } from "./VirtualizedOption";
import { VirtualizedSeparatorStyled } from "./VirtualizedSeparatorStyled";

interface SelectMenuProps<Option extends BaseOptionType> {
    /**
     * Only applicable to multiselects.
     */
    allSelected: boolean;
    /**
     * Only applicable to multiselects.
     */
    enableSelectAll: boolean;

    flyoutProps: FlyoutProps;
    /**
     * This is the formatOption from the user, or the default
     */
    formatOption: FormatOption<Option>;
    getItemProps: (
        ...args: Parameters<PropGetters<Option>["getItemProps"]>
    ) => HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>;
    getMenuProps: (
        ...args: Parameters<PropGetters<Option>["getMenuProps"]>
    ) => HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>;
    /**
     * Used to provide a deterministic way to identify each item in the following cases:
     * - formatOption not provided so default formatting will apply
     * - virtualization is on, each item will have a key based on this function
     * - custom option formatting, key will be passed up through params, a custom key could be used here
     */
    getOptionKey: (option: Option) => Key;
    highlightedIndex: number;
    /**
     * Only applicable to comboboxes.
     */
    inputValue: string;
    isLoading: boolean;
    isOpen: boolean;
    maxMenuHeight: number;
    maxOptionsUntilVirtualization: number;
    // Can we make this reference their actual types? Right now that'd be a circular ref.
    optionToString: (option: Option) => string;
    options: Option[];
    /**
     * Only applicable to multiselects.
     */
    selectedItems: Option[];
    targetWidth: number;

    extraMenuProps?: HTMLAttributes<HTMLDivElement>;

    isOptionDisabled?: (option: Option) => boolean;

    menuWidth?: "match-control" | "auto" | "auto-grow" | "auto-shrink";

    optionHeight?: number;

    selectedOption?: Option | null;
}

export const SelectMenu = <Option extends BaseOptionType>({
    extraMenuProps,
    flyoutProps,
    formatOption,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    isLoading,
    isOpen,
    isOptionDisabled,
    menuWidth,
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    optionHeight: providedOptionHeight,
    options,
    targetWidth,
    allSelected,
    enableSelectAll,
    inputValue,
    selectedItems,
    selectedOption,
    getOptionKey,
}: SelectMenuProps<Option>) => {
    const { i18n } = useI18n();
    const themeMode = useCombinedThemeMode();

    const selectAllOptionHeight = menuTheme.optionHeight.normal(themeMode);
    const optionHeight = providedOptionHeight || menuTheme.optionHeight.normal(themeMode);
    const separatorHeight = menuTheme.separatorHeight(themeMode);
    const showAsListbox = isOpen && options.length > 0;
    const shouldVirtualize = options.length > maxOptionsUntilVirtualization;
    const listRef = useRef<VariableSizeList>(null);

    const {
        // Pull this off - we'll reattach our own later.
        "aria-labelledby": _unused,
        ...restMenuProps
    } = getMenuProps(
        {
            "aria-label": showAsListbox ? i18n._(/*i18n*/ "listbox contents") : undefined,
            id: flyoutProps.id,
            // We have to turn off the listbox role when it doesn't have any children. This is
            // from the spec - a listbox MUST have children of the role "option" | "group".
            role: showAsListbox ? "listbox" : "none",

            ...extraMenuProps,
        },
        {
            // The Flyout doesn't attach itself until a tick later. Trust us downshift, we're good :)
            suppressRefError: true,
        }
    );

    const isOptionSelected = useCallback(
        (option: Option) => {
            return selectedOption === option || selectedItems.includes(option);
        },
        [selectedItems, selectedOption]
    );

    const getRowSize = (index: number) => {
        if (isSelectAllMarkerOption(options[index])) {
            return selectAllOptionHeight + separatorHeight;
        } else if (isSelectV2OptionSeparator(options[index])) {
            return separatorHeight;
        }
        return optionHeight;
    };

    const getRenderedOption: FormatOption<Option> = useCallback(
        (option, index, highlightedIndex, extraProps) => {
            const isSelectAllOption = isSelectAllMarkerOption(option);
            if (enableSelectAll && isSelectAllOption) {
                const propsWithoutHeight = { ...extraProps };
                delete propsWithoutHeight.style?.height;
                return (
                    <Fragment key={selectAllMarkerOption.value}>
                        <SelectAllOption
                            checkedState={
                                selectedItems.length === 0 ? "unchecked" : allSelected ? "checked" : "indeterminate"
                            }
                            {...extraProps}
                        />
                        <VirtualizedSeparatorStyled
                            shouldVirtualize={shouldVirtualize}
                            optionHeight={selectAllOptionHeight}
                        />
                    </Fragment>
                );
            } else if (isCreatableMarkerOption(option)) {
                return (
                    <CreatableOption
                        inputValue={inputValue}
                        optionHeight={optionHeight}
                        {...extraProps}
                        key={creatableMarkerOption.value}
                    />
                );
            } else if (isSelectV2OptionSeparator(option)) {
                return (
                    <VirtualizedSeparatorStyled
                        shouldVirtualize={shouldVirtualize}
                        optionHeight={separatorHeight}
                        top={extraProps.style?.top}
                        key={`${selectV2OptionSeparator.value}-${index}`}
                    />
                );
            } else {
                return formatOption(option, index, highlightedIndex, extraProps, isOptionSelected(option), {
                    inputValue,
                    isDisabled: isOptionDisabled?.(option) ?? false,
                });
            }
        },
        [
            allSelected,
            enableSelectAll,
            formatOption,
            inputValue,
            isOptionSelected,
            isOptionDisabled,
            optionHeight,
            selectAllOptionHeight,
            selectedItems.length,
            separatorHeight,
            shouldVirtualize,
        ]
    );

    const itemData = useMemo(
        () => ({
            getItemProps,
            getRenderedOption,
            highlightedIndex,
            options,
            getOptionKey,
            isOptionSelected,
            metadata: { inputValue },
        }),
        [getItemProps, getOptionKey, getRenderedOption, highlightedIndex, isOptionSelected, options, inputValue]
    );

    const itemKey = useCallback(
        (index: number) => {
            const option = options[index];
            if (isSelectV2OptionSeparator(option)) return `${option.value}-${index}`;
            if (isSelectAllMarkerOption(option) || isCreatableMarkerOption(option)) return option.value;
            return getOptionKey(option);
        },
        [getOptionKey, options]
    );

    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [options]);

    useEffect(() => {
        if (highlightedIndex >= 0) listRef.current?.scrollToItem(highlightedIndex);
    }, [highlightedIndex]);

    const menu = isOpen ? (
        <Fragment>
            {shouldVirtualize ? (
                <VariableSizeListStyled
                    height={maxMenuHeight}
                    width="100%"
                    itemCount={options.length}
                    itemSize={getRowSize}
                    itemKey={itemKey}
                    ref={listRef}
                    itemData={itemData}
                >
                    {VirtualizedOption}
                </VariableSizeListStyled>
            ) : (
                <Fragment>
                    {options.map((option, index) =>
                        getRenderedOption(
                            option,
                            index,
                            highlightedIndex,
                            getItemProps({
                                item: option,
                                index,
                                onClick: stopPropagationHandler,
                                key: getOptionKey(option),
                            }) as OptionSpreadProps,
                            isOptionSelected(option),
                            {
                                inputValue,
                                isDisabled: isOptionDisabled?.(option) ?? false,
                            }
                        )
                    )}
                </Fragment>
            )}
            {isLoading && <LoadingOption />}
            {!isLoading && (options.length === 0 || (options.length === 1 && isSelectAllMarkerOption(options[0]))) && (
                <NoResultsMessage height={optionHeight} themeMode={themeMode} />
            )}
        </Fragment>
    ) : null;

    // when menuWidth options were introduced, this became
    // necessary in order to not break the existing placement API
    const getMenuPlacement = (placement: FlyoutPlacement | undefined) => {
        switch (placement) {
            case "bottom":
                return "bottom-start";
            case "top":
                return "top-start";
            default:
                return "auto";
        }
    };

    return (
        <Flyout
            {...flyoutProps}
            {...restMenuProps}
            placement={getMenuPlacement(flyoutProps?.placement)}
            style={{
                ...restMenuProps.style,
                width: menuWidth === "match-control" ? `${targetWidth}px` : `unset`,
                minWidth: menuWidth === "auto-grow" ? `${targetWidth}px` : `unset`,
                maxWidth: menuWidth === "auto-shrink" ? `${targetWidth}px` : `unset`,
            }}
            css={
                isOpen
                    ? css`
                          padding: 4px 0;
                          overflow-y: auto;
                          overflow-x: hidden;
                          background-color: ${menuTheme.backgroundColor.rest(themeMode)};
                          box-shadow: ${menuTheme.boxShadow(themeMode)};
                          border-radius: ${menuTheme.borderRadius(themeMode)};
                          max-height: ${maxMenuHeight}px;
                          box-sizing: border-box;
                          outline: none;
                          ${shouldVirtualize
                              ? `
                              padding: 0;
                              `
                              : `
                              `}
                      `
                    : undefined
            }
        >
            {menu}
        </Flyout>
    );
};
